import type { IHelpForm } from '~components/HelpForm/HelpForm';

import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { useTgnBanner } from '~/components/TgnBanner/useTgnBanner';
import { useTgnDialog } from '~components/TgnDialog/useTgnDialog';
import { useWindowSize } from '@vueuse/core';

interface PageLabel {
  label?: string;
  title?: string;
  description?: string;
  icon?: string;
  actions?: { label: string; icon: string; fn: () => any; show: boolean }[];
}

export const useLayoutStore = defineStore(
  'MY_SCAN_SOURCE_LAYOUT_STORE',
  () => {
    const route = useRoute();
    const useBanner = useTgnBanner();
    const COMPANY_STORE = useCompanyStore();
    const { banner, isBannerVisible } = useBanner;
    const { companyDocument, companyName } = storeToRefs(COMPANY_STORE);

    // state:
    const useDialog = useTgnDialog();
    const hasFloatingMenu = ref<boolean>(true);
    const hasPageLabel = ref<boolean>(false);
    const isLoading = ref<boolean>(false);
    const helpModal = ref<boolean>(false);
    const showingEconomicGroupModal = ref(false);
    const helpFields = ref<IHelpForm>({
      companyDocument: '',
      email: '',
      fullName: '',
      message: '',
      phoneNumber: '',
      reasonCantAccess: false,
      reasonChangeEmail: false,
      reasonMissedPassword: false,
      reasonOthers: false,
    });
    const wasUpdatedTitle = ref<string>('');

    const pageLabel = ref<PageLabel>({});
    const theme = ref('');
    const themeIcon = ref();

    // getters...
    const { width } = useWindowSize();
    const isMobile = computed((): boolean => width.value <= 768);
    const isMiddleScreen = computed(
      (): boolean => width.value <= 1024 && width.value > 768,
    );
    const isDarkMode = computed(
      () =>
        window.matchMedia &&
        window.matchMedia('(prefers-color-scheme: dark)').matches &&
        theme.value === 'dark',
    );

    // onBeforeMount(() => {
    //   isDarkMode.value ? defineDarkTheme() : defineLightTheme();
    // });

    watchEffect(() => {
      if (route.name && route.name !== 'index') {
        if (route.name === 'dashboard' && wasUpdatedTitle.value !== 'dashboard') {
          setTimeout(() => useBanner.showMainContent(), 100);
          showPageLabel();
          useBanner.hideMainContent();
          useBanner.restoreDefault();
          wasUpdatedTitle.value = 'dashboard';
        }

        if (route.name !== 'dashboard' && wasUpdatedTitle.value === 'dashboard') {
          setTimeout(() => useBanner.showMainContent(), 100);
          hidePageLabel();
          pageLabel.value.label = undefined;
          pageLabel.value.title = undefined;
          useBanner.hideMainContent();
          useBanner.setBannerTitle(capitalizeAll(companyName.value));
          useBanner.setBannerSubtitle(companyDocument.value.masked);
          wasUpdatedTitle.value = route.name as string;
        }
      }

      if (companyDocument.value && route.name !== 'dashboard') {
        useBanner.setBannerTitle(capitalizeAll(companyName.value));
        useBanner.setBannerSubtitle(companyDocument.value.masked);
      }
    });

    return {
      ...useDialog,
      theme,
      themeIcon,
      isDarkMode,
      toggleTheme,

      banner,
      hasFloatingMenu,
      hasPageLabel,
      helpFields,
      helpModal,
      isLoading,
      isMobile,
      isMiddleScreen,
      pageLabel,
      showingEconomicGroupModal,
      hideEconomicGroupModal,
      hideFloatingNavbar,
      hideHelpModal,
      hidePageLabel,
      restoreDefault,
      setIsLoadingOff,
      setIsLoadingOn,
      setPageDescription,
      setPageLabel,
      setPageLabelAction,
      setPageIcon,
      setPageTitle,
      showEconomicGroupModal,
      showFloatingNavbar,
      showHelpModal,
      showPageLabel,

      isBannerVisible,
      showBanner: useBanner.showBanner,
      hideBanner: useBanner.hideBanner,
      setBannerBgColor: useBanner.setBannerBgColor,
      setBannerBgImage: useBanner.setBannerBgImage,
      setBannerDescription: useBanner.setBannerDescription,
      setBannerSubtitle: useBanner.setBannerSubtitle,
      setBannerTitle: useBanner.setBannerTitle,
      setBannerVariant: useBanner.setBannerVariant,
    };

    // actions:
    function hideFloatingNavbar(): void {
      hasFloatingMenu.value = false;
    }
    function showFloatingNavbar(): void {
      hasFloatingMenu.value = true;
    }
    function hidePageLabel(): void {
      hasPageLabel.value = false;
    }
    function showPageLabel(): void {
      hasPageLabel.value = true;
    }
    function setPageLabel(label: string): void {
      pageLabel.value = { ...pageLabel.value, label };
    }
    function setPageIcon(icon: string): void {
      pageLabel.value = { ...pageLabel.value, icon };
    }
    function setPageTitle(title: string): void {
      if (!title) return;

      pageLabel.value = { ...pageLabel.value, title };
    }
    function setPageDescription(description: string): void {
      pageLabel.value = { ...pageLabel.value, description };
    }

    function setPageLabelAction(actions: PageLabel['actions']): void {
      pageLabel.value = { ...pageLabel.value, actions };
    }

    function setIsLoadingOn(): void {
      isLoading.value = true;
    }

    function setIsLoadingOff(): void {
      setTimeout(() => {
        isLoading.value = false;
      }, 1000);
    }

    function restoreDefault(): void {
      hasFloatingMenu.value = true;
      hasPageLabel.value = true;
      pageLabel.value = {};
      useBanner.showBanner();
      useBanner.restoreDefault();
    }

    function showHelpModal() {
      helpModal.value = true;
    }

    function hideHelpModal() {
      helpModal.value = false;
      helpFields.value = {
        companyDocument: '',
        email: '',
        fullName: '',
        message: '',
        phoneNumber: '',
        reasonCantAccess: false,
        reasonChangeEmail: false,
        reasonMissedPassword: false,
        reasonOthers: false,
      };
    }

    function showEconomicGroupModal() {
      showingEconomicGroupModal.value = true;
    }

    function hideEconomicGroupModal() {
      showingEconomicGroupModal.value = false;
    }

    function defineDarkTheme() {
      document.documentElement.classList.add('dark');
      themeIcon.value = 'material-symbols-light:wb-sunny-outline-rounded';
      theme.value = 'dark';
    }

    function defineLightTheme() {
      document.documentElement.classList.remove('dark');
      themeIcon.value = 'material-symbols-light:dark-mode-outline-rounded';
      theme.value = 'light';
    }

    function toggleTheme() {
      theme.value === 'dark' ? defineLightTheme() : defineDarkTheme();
    }
  },
  { persist: true },
);
